import type { SimpleDateDTO } from "@wkt/api/checkin/checkinDTO";

function isCompleteSimpleDate(value: unknown): value is SimpleDateDTO {
  if (!value || typeof value !== "object") {
    return false;
  }

  const simpleDate = value as SimpleDateDTO;
  return Boolean(simpleDate.year && simpleDate.month && simpleDate.day);
}

function toUtcSimpleDate(value: number | string): SimpleDateDTO | null {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return {
    year: parsedDate.getUTCFullYear(),
    month: parsedDate.getUTCMonth() + 1,
    day: parsedDate.getUTCDate(),
  };
}

export function parseSimpleDateValue(
  value?: string | number | SimpleDateDTO | null,
): SimpleDateDTO | null {
  if (value == null) {
    return null;
  }

  if (isCompleteSimpleDate(value)) {
    return {
      year: value.year,
      month: value.month,
      day: value.day,
    };
  }

  if (typeof value === "number") {
    return toUtcSimpleDate(value);
  }

  if (/^\d+$/.test(value)) {
    return toUtcSimpleDate(Number(value));
  }

  // Calendar days are day-only values. If the payload already includes a
  // literal YYYY-MM-DD prefix, preserve that exact day and ignore any time part.
  const simpleDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/);
  if (simpleDateMatch) {
    const [, year, month, day] = simpleDateMatch;
    return {
      year: Number(year),
      month: Number(month),
      day: Number(day),
    };
  }

  return toUtcSimpleDate(value);
}

export function toSimpleDateKey(
  value?: string | number | SimpleDateDTO | null,
): string | null {
  const normalizedDate = parseSimpleDateValue(value);
  if (!normalizedDate) {
    return null;
  }

  return `${normalizedDate.year}-${String(normalizedDate.month).padStart(
    2,
    "0",
  )}-${String(normalizedDate.day).padStart(2, "0")}`;
}
